import { useStore } from '@vromo/mobx-react/mobx-react';
import { OVERFLOW_CONFIRMED } from '@vromo/models/CourierInfo';
import TrackingLinkStore from '@vromo/tracking-link/TrackingLinkStore';
import Icon from '@vromo/ui/components/FontAwesome';
import currencies from '@vromo/vendors/job-request/currencies.json';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { qaLabelTrackingLink } from '../../../../../common-qa-labels/tracking-link';
import styles from '../../orderTracking.style';
import { byMostRecent } from '@vromo/utils/arrayUtils';

const OrderDetails = observer(() => {
    const { t } = useTranslation();
    const rootStore = useStore<TrackingLinkStore>('rootStore');
    const job = rootStore.jobs.first()!;
    const pickupAddress = job?.tasks?.items[0]?.zone?.address || 'Unknown';
    const deliveryAddress = job?.tasks?.items.at(-1)?.zone?.address || 'Unknown';
    const currencySymbol =
        currencies[job?.payable?.currency?.toUpperCase() as keyof typeof currencies]?.symbol;
    const orderValue = job?.payable?.order || 0;

    const confirmedOverflowItems = job?.courierInfo
        ?.filter((item) => item.status === OVERFLOW_CONFIRMED)
        .sort(byMostRecent);

    const fleetJobId = confirmedOverflowItems?.length ? confirmedOverflowItems[0]?.fleetJobId : '';

    const totalQuantity = job?.lines?.items.reduce((sum, obj) => sum + (obj?.quantity || 0), 0);

    return (
        <>
            <div style={styles.title} data-testid={qaLabelTrackingLink.orderDetailsTitle}>
                {t('orderDetails')}
            </div>
            <div style={styles.orderName}>
                {job.site?.contact?.name}
                {!!job.external && ` Order #${job.external}`}
            </div>
            <div style={styles.orderName} data-testid={qaLabelTrackingLink.jobName}>
                {job.name}
            </div>
            {!!fleetJobId && job.status !== 'open' &&  (
                <div style={styles.orderName} data-testid={qaLabelTrackingLink.jobExternal}>
                    {fleetJobId}
                </div>
            )}
            {job.contact?.name && (
                <>
                    <div
                        style={styles.detailSubtitle}
                        data-testid={qaLabelTrackingLink.userContactTitle}>
                        {'Contact Name'}
                    </div>
                    <div style={styles.row}>
                        <Icon icon={'user-alt'} />
                        <div
                            style={styles.orderDetail}
                            data-testid={qaLabelTrackingLink.userContact}>
                            {job.contact?.name}
                        </div>
                    </div>
                </>
            )}
            <div style={styles.detailSubtitle} data-testid={qaLabelTrackingLink.pickupAddressTitle}>
                {t('pickupAddress')}
            </div>
            <div style={styles.row}>
                <Icon icon={'building'} />
                <div style={styles.orderDetail} data-testid={qaLabelTrackingLink.pickupAddress}>
                    {pickupAddress}
                </div>
            </div>
            <div
                style={styles.detailSubtitle}
                data-testid={qaLabelTrackingLink.deliveryAddressTitle}>
                {t('deliveryAddress')}
            </div>
            <div style={styles.row}>
                <Icon icon={'home'} />
                <div style={styles.orderDetail} data-testid={qaLabelTrackingLink.deliveryAddress}>
                    {deliveryAddress}
                </div>
            </div>
            <div style={styles.detailSubtitle} data-testid={qaLabelTrackingLink.instructionsTitle}>
                {t('dropoffOption')}
            </div>
            <div style={styles.row}>
                <Icon icon={'user-alt'} />
                <div style={styles.orderDetail} data-testid={qaLabelTrackingLink.instructions}>
                    {job?.instructions || 'None'}
                </div>
            </div>
            <div style={styles.lineDivider} />
            <div style={styles.itemDetailsRow}>
                <div style={styles.columnPadding}>
                    <div style={styles.subtitle} data-testid={qaLabelTrackingLink.tipTitle}>
                        {t('driverTip')}
                    </div>
                    <div style={styles.tip} data-testid={qaLabelTrackingLink.tip}>
                        {job?.payable?.tip
                            ? `${currencySymbol}${(job?.payable?.tip / 100).toFixed(2)}`
                            : 'N/A'}
                    </div>
                </div>
                <div style={styles.columnPadding}>
                    <div style={styles.subtitle} data-testid={qaLabelTrackingLink.quantityTitle}>
                        {t('numberOfItems')}
                    </div>
                    <div style={styles.itemField} data-testid={qaLabelTrackingLink.quantity}>
                        {totalQuantity || 'N/A'}
                    </div>
                </div>
                {!!orderValue && (
                    <div>
                        <div style={styles.subtitle} data-testid={qaLabelTrackingLink.totalTitle}>
                            {t('subtotal')}
                        </div>
                        <div style={styles.itemField} data-testid={qaLabelTrackingLink.total}>
                            {orderValue
                                ? `${currencySymbol}${(orderValue / 100).toFixed(2)}`
                                : 'N/A'}
                        </div>
                    </div>
                )}
            </div>
            <div style={styles.lineDivider} />
        </>
    );
});

export default OrderDetails;
